import React, { useMemo } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { observer } from 'mobx-react-lite';
import LinechartOfBusinessData, {
    mergeBusinessLineApexOptions,
    createDashboardBusinessLineApexOptions,
} from '../../component/charts/line-chart-of-business-data';
import PieChartOfBusinessData from '../../component/charts/pie-chart-of-business-data';
import i18n from '../../i18n';
import { formatEUR } from '../../constants/tool.fn';
import {
    getPeriodFromDates,
} from '../../component/period-date-filter.component';
import { getGroupRevenueComparePeriodLabelKey } from '../../constants/group-revenue-compare-label';
import groupRevenueAnalysisState from './group-revenue-analysis-state';
import { GroupRevenueKpiMomSubtitle } from './group-revenue-kpi-mom-subtitle.component';
import { GroupTotalRevenueKpiCard } from './group-total-revenue-kpi-card.component';
import { getPieChartColorsByValues } from '../../themes/tokens/data-chart-tokens';
import { ReactComponent as StoreRankingIcon } from '../../static/images/svg/bxs_store.svg';
import {
    GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX,
    GROUP_REVENUE_TOTAL_TAB_FILL_VIEWPORT_MIN_HEIGHT_PX,
    GROUP_REVENUE_TOTAL_TAB_TWO_COLUMN_MIN_WIDTH_PX,
} from './group-revenue-analysis-layout';

/** 图表次要文字与网格线使用主题/语义色，避免 Hex 硬编码 */
const chartLabelColor = 'var(--text-neutral-500)';    // 坐标轴、图例等文字
const chartGridColor = 'var(--stroke-neutral-800-a25)';  // 网格线

/**
 * 集团营业总额 Tab：KPI 卡片、营收趋势折线图、门店营收排名、支付方式占比饼图
 */
const GroupTotalRevenue: React.FC = observer(() => {
    const theme = useTheme();
    /** 宽屏双栏：≥1280px 时左 KPI+折线、右排名+饼图并排；KPI 三卡仍横排 */
    const isTwoColumnLayout = useMediaQuery(
        `(min-width: ${GROUP_REVENUE_TOTAL_TAB_TWO_COLUMN_MIN_WIDTH_PX}px)`,
    );
    /** 大屏拉满：需同时满足足够高度与双栏宽度，避免平板横屏被挤成窄双栏 */
    const fillsViewportOnLargeScreen = useMediaQuery(
        `(min-height: ${GROUP_REVENUE_TOTAL_TAB_FILL_VIEWPORT_MIN_HEIGHT_PX}px) and (min-width: ${GROUP_REVENUE_TOTAL_TAB_TWO_COLUMN_MIN_WIDTH_PX}px)`,
    );
    const state = groupRevenueAnalysisState;
    const dashboard = state.dashboard;
    const trend = state.trend;
    const loading = state.loadingGroupTotalRevenue;

    const successColor = theme.palette.success?.main ?? theme.palette.primary.main;   // 成功色（绿）
    const errorColor = theme.palette.error?.main ?? theme.palette.primary.main;       // 错误色（红）

    const paymentBreakdown = dashboard?.payment_breakdown ?? [];  // 支付方式占比数据

    const pieChartSeries = paymentBreakdown.map((p) => {
        const num = parseFloat(String(p.percent).replace('%', '')) || 0;
        return num;
    });

    const pieLabels = paymentBreakdown.map((p) => p.name);

    /** 饼图与底部自定义图例：按占比 value 名次取色 */
    const pieColors = useMemo(
        () => getPieChartColorsByValues(pieChartSeries, pieLabels),
        [paymentBreakdown],
    );

    /** 营业总额趋势折线数据（单序列） */
    const totalRevenueTrendSeries = useMemo(
        () => [
            {
                name: i18n.t('group_revenue_analysis_group_total'),
                data: trend?.series?.map((p) => p.revenue) ?? [],
            },
        ],
        [trend?.series, i18n.language],
    );

    /** 趋势图横轴原始 x（Apex 内格式化为 MM/DD 或 HH:mm 并抽稀） */
    const totalRevenueTrendRawXLabels = useMemo(
        () => trend?.series?.map((p) => p.x) ?? [],
        [trend?.series],
    );

    /** 趋势图 Apex options：基底 + 本页坐标轴/图例样式 */
    const totalRevenueTrendApexOptions = useMemo(
        () =>
            mergeBusinessLineApexOptions(
                createDashboardBusinessLineApexOptions(theme, {
                    rawXLabels: totalRevenueTrendRawXLabels,
                    granularity: trend?.granularity,
                    seriesCount: totalRevenueTrendSeries.length,
                    height: fillsViewportOnLargeScreen ? '100%' : GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX,
                    strokeWidth: 2,
                }),
                {
                    xaxis: {
                        labels: {
                            style: { colors: chartLabelColor, fontSize: '12px' },
                        },
                    },
                    yaxis: {
                        labels: {
                            style: { colors: chartLabelColor, fontSize: '12px' },
                            formatter: (value: number) => value.toString(),
                        },
                    },
                    grid: {
                        borderColor: chartGridColor,
                        strokeDashArray: 0,
                    },
                    legend: {
                        /** Apex 运行时支持宽高/圆角，与 @types/apexcharts 中 legend.markers 声明不完全一致 */
                        markers: { width: 8, height: 2, radius: 0 } as any,
                    },
                },
            ),
        [
            theme,
            totalRevenueTrendRawXLabels,
            trend?.granularity,
            totalRevenueTrendSeries.length,
            chartLabelColor,
            chartGridColor,
            trend?.series,
            i18n.language,
            fillsViewportOnLargeScreen,
        ],
    );

    /** 门店排名：按 revenue 降序，取 breakdown */
    const storeRanking = dashboard?.breakdown
        ? [...dashboard.breakdown].sort((a, b) => b.revenue - a.revenue)
        : [];

    /** 环比前缀：本周/本日/本月；自定义日期不展示环比行 */
    const comparePeriodKey = getGroupRevenueComparePeriodLabelKey(
        getPeriodFromDates(state.startDate, state.endDate),
    );
    const comparePrefix = comparePeriodKey ? i18n.t(comparePeriodKey) : undefined;

    /** 加载态：模块比例与下方「三 KPI + 折线图 | 排名 + 饼图」一致 */
    if (loading) {
        return (
            <LoadingSkeleton
                fillsViewportOnLargeScreen={fillsViewportOnLargeScreen}
                isTwoColumnLayout={isTwoColumnLayout}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isTwoColumnLayout ? 'row' : 'column',
                gap: 3,
                p: 1,
                boxSizing: 'border-box',
                alignItems: 'stretch',
                ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: '100%' } : {}),
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    width: isTwoColumnLayout ? undefined : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    ...(fillsViewportOnLargeScreen ? { minHeight: 0 } : {}),
                }}
            >
                <Box sx={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                    <GroupTotalRevenueKpiCard
                        title={i18n.t('group_revenue_analysis_total_revenue')}
                        value={dashboard ? formatEUR(dashboard.total_revenue) : '—'}
                        subtitle={(
                            <GroupRevenueKpiMomSubtitle
                                percentRaw={dashboard?.mom_percent}
                                comparePrefix={comparePrefix}
                                successColor={successColor}
                                errorColor={errorColor}
                            />
                        )}
                    />
                    <GroupTotalRevenueKpiCard
                        title={i18n.t('group_revenue_analysis_total_orders')}
                        value={dashboard?.total_orders ?? '—'}
                        subtitle={(
                            <GroupRevenueKpiMomSubtitle
                                percentRaw={dashboard?.orders_mom_percent}
                                comparePrefix={comparePrefix}
                                successColor={successColor}
                                errorColor={errorColor}
                            />
                        )}
                    />
                    <GroupTotalRevenueKpiCard
                        title={i18n.t('group_revenue_analysis_avg_order_amount')}
                        value={dashboard ? formatEUR(dashboard.avg_order_amount) : '—'}
                        subtitle={(
                            <GroupRevenueKpiMomSubtitle
                                percentRaw={dashboard?.avg_order_amount_mom_percent}
                                comparePrefix={comparePrefix}
                                successColor={successColor}
                                errorColor={errorColor}
                            />
                        )}
                    />
                </Box>

                <Box
                    sx={{
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: '16px',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        boxShadow: '0px 0px 4px var(--shadow-25)',
                        ...(fillsViewportOnLargeScreen
                            ? { flex: 1, minHeight: 0 }
                            : { flexShrink: 0 }),
                    }}
                >
                    <Typography variant="h5" sx={{ color: 'var(--text-primary-300)', fontWeight: 'bold', flexShrink: 0, textAlign: 'center', width: '100%' }}>
                        {i18n.t('group_revenue_analysis_revenue_chart_title')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: chartLabelColor, flexShrink: 0 }}>
                        {i18n.t('group_revenue_analysis_amount')}
                    </Typography>
                    <Box
                        sx={{
                            position: 'relative',
                            ...(fillsViewportOnLargeScreen
                                ? { flex: 1, minHeight: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX }
                                : {
                                    minHeight: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX,
                                    height: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX,
                                }),
                        }}
                    >
                        <LinechartOfBusinessData
                            series={totalRevenueTrendSeries}
                            apexOptions={totalRevenueTrendApexOptions}
                            height={fillsViewportOnLargeScreen ? '100%' : GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX}
                            rootId="line-chart-of-business-data-compare"
                            rawXLabels={totalRevenueTrendRawXLabels}
                            xAxisGranularity={trend?.granularity}
                        />
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    width: isTwoColumnLayout ? 440 : '100%',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    ...(fillsViewportOnLargeScreen ? { minHeight: 0 } : {}),
                }}
            >
                {/* 门店营收排名：大屏随左栏等高；列表区可滚动，饼图区保持自然高度 */}
                <Box
                    sx={{
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: '16px',
                        p: 1,
                        boxShadow: '0px 0px 4px var(--shadow-25)',
                        display: 'flex',
                        flexDirection: 'column',
                        ...(fillsViewportOnLargeScreen
                            ? { flex: 1, minHeight: 0, overflow: 'hidden' }
                            : { flexShrink: 0 }),
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, px: 2, py: 1, flexShrink: 0 }}>
                        <Box component="span" sx={{ color: 'var(--icon-primary-300)', display: 'inline-flex', flexShrink: 0 }}>
                            <StoreRankingIcon style={{ width: 24, height: 24 }} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary-300)', textAlign: 'center' }}>
                            {i18n.t('group_revenue_analysis_store_ranking')}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            mt: 1,
                            pr: 0.5,
                            ...(fillsViewportOnLargeScreen
                                ? { flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }
                                : {}),
                        }}
                    >
                        {storeRanking.map((store) => {
                            const firstRevenue = storeRanking[0]?.revenue ?? 0;
                            /** 相对第一名营收比例（0–100），用于左侧紫色进度条宽度 */
                            const barWidth = firstRevenue > 0 ? (store.revenue / firstRevenue) * 100 : 0;
                            /** 第一名进度拉满时四角圆角，否则仅左侧圆角 */
                            const isFullWidth = barWidth >= 100;
                            return (
                                <Box
                                    key={store.restaurant_id}
                                    sx={{
                                        position: 'relative',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexShrink: 0,
                                        p: '16px',
                                        borderRadius: '16px',
                                        bgcolor: 'var(--fill-neutral-200)',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: `${barWidth}%`,
                                            bgcolor: 'var(--fill-primary-200)',
                                            ...(isFullWidth ? { borderRadius: '16px' } : { borderTopLeftRadius: '16px', borderBottomLeftRadius: '16px' }),
                                            zIndex: 0,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            position: 'relative',
                                            zIndex: 1,
                                            color: 'var(--text-dark)',
                                            fontSize: 16,
                                            lineHeight: '24px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1,
                                            minWidth: 0,
                                            mr: 1,
                                        }}
                                    >
                                        {store.restaurant_name ?? store.restaurant_id}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            position: 'relative',
                                            zIndex: 1,
                                            color: 'var(--text-primary-300)',
                                            fontSize: 20,
                                            lineHeight: '24px',
                                            fontWeight: 500,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {formatEUR(store.revenue)}
                                    </Typography>
                                </Box>
                            );
                        })}
                        {storeRanking.length === 0 && !loading && (
                            <Typography variant="body2" sx={{ color: chartLabelColor, py: 2, textAlign: 'center' }}>
                                {i18n.t('general_text_no_data')}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: '16px',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        boxShadow: '0px 0px 4px var(--shadow-25)',
                        flexShrink: 0,
                    }}
                >
                    <Typography variant="subtitle1" sx={{ color: 'var(--text-primary-300)', textAlign: 'center', fontWeight: 600 }}>
                        {i18n.t('group_revenue_analysis_revenue_ratio')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {paymentBreakdown.length === 0 && !loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 260, height: 260 }}>
                                <Typography variant="body2" sx={{ color: chartLabelColor }}>
                                    {i18n.t('general_text_no_data')}
                                </Typography>
                            </Box>
                        ) : (
                            <PieChartOfBusinessData
                                data={pieChartSeries}
                                labels={pieLabels}
                                titleText=""
                                layout="dialog"
                                height={260}
                                dialogDataLabelMode="labelPercent"
                                dialogDataLabelFontSize="16px"
                                colors={pieColors}
                                containerSx={{
                                    width: 260,
                                    height: 260,
                                    minWidth: 260,
                                    maxWidth: 260,
                                }}
                            />
                        )
                        }
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            p: 2,
                            overflowX: 'auto',
                            flexWrap: 'nowrap',
                        }}
                    >
                        {paymentBreakdown.map((item, idx) => (
                            <Box key={item.name} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        bgcolor: pieColors[idx % pieColors.length]
                                    }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ color: 'var(--text-dark)' }}>
                                        {item.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'var(--text-dark)' }}>
                                        {formatEUR(item.revenue)}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                    </Box>
                </Box>
            </Box>
        </Box>
    );
});

export default GroupTotalRevenue;


function LoadingSkeleton({
    fillsViewportOnLargeScreen,
    isTwoColumnLayout,
}: {
    fillsViewportOnLargeScreen: boolean;
    isTwoColumnLayout: boolean;
}) {
    const kpiCardH = 132;
    const rankRowH = 44;
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isTwoColumnLayout ? 'row' : 'column',
                gap: 3,
                p: 1,
                boxSizing: 'border-box',
                alignItems: 'stretch',
                ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: '100%' } : {}),
            }}
        >
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    width: isTwoColumnLayout ? undefined : '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    ...(fillsViewportOnLargeScreen ? { minHeight: 0 } : {}),
                }}
            >
                <Box sx={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                    <Skeleton variant="rounded" sx={{ flex: 1, height: kpiCardH, borderRadius: 2 }} />
                    <Skeleton variant="rounded" sx={{ flex: 1, height: kpiCardH, borderRadius: 2 }} />
                    <Skeleton variant="rounded" sx={{ flex: 1, height: kpiCardH, borderRadius: 2 }} />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: 2,
                        p: 2,
                        boxShadow: '0px 0px 4px var(--shadow-25)',
                        ...(fillsViewportOnLargeScreen
                            ? { flex: 1, minHeight: 0 }
                            : {}),
                    }}
                >
                    <Skeleton variant="text" width="42%" height={32} />
                    <Skeleton variant="text" width="18%" height={20} />
                    <Skeleton
                        variant="rounded"
                        sx={{
                            borderRadius: 1,
                            ...(fillsViewportOnLargeScreen
                                ? { flex: 1, minHeight: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX }
                                : { height: GROUP_REVENUE_LINE_CHART_MIN_HEIGHT_PX }),
                        }}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    width: isTwoColumnLayout ? 400 : '100%',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    ...(fillsViewportOnLargeScreen ? { minHeight: 0 } : {}),
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2,
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: 2,
                        boxShadow: '0px 0px 4px var(--shadow-25)',
                        ...(fillsViewportOnLargeScreen
                            ? { flex: 1, minHeight: 0, overflow: 'hidden' }
                            : {}),
                    }}
                >
                    <Skeleton variant="text" width="55%" height={28} sx={{ alignSelf: 'center', mb: 1 }} />
                    {[0, 1, 2, 3, 4].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            height={rankRowH}
                            sx={{ borderRadius: 2.5, mb: 1, flexShrink: 0 }}
                        />
                    ))}
                </Box>
                <Box
                    sx={{
                        flexShrink: 0,
                        p: 2,
                        bgcolor: 'var(--fill-neutral-100)',
                        borderRadius: 2,
                        boxShadow: '0px 0px 4px var(--shadow-25)',
                    }}
                >
                    <Skeleton variant="text" width="48%" height={24} sx={{ alignSelf: 'center', mb: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                        <Skeleton variant="circular" width={260} height={260} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mt: 1.5, justifyContent: 'flex-start', flexWrap: 'nowrap', overflow: 'hidden' }}>
                        <Skeleton variant="rounded" width={72} height={36} sx={{ flexShrink: 0 }} />
                        <Skeleton variant="rounded" width={72} height={36} sx={{ flexShrink: 0 }} />
                        <Skeleton variant="rounded" width={72} height={36} sx={{ flexShrink: 0 }} />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}